import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { MDBIcon } from 'mdb-react-ui-kit';

// Styled Components
const slideInUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideOutDown = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(100%);
    opacity: 0;
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
`;

const ChatContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const ChatButton = styled.button`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #042e2e, #065454);
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(4, 46, 46, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 25px rgba(4, 46, 46, 0.6);
  }

  ${props => props.$hasUnread && css`
    &::after {
      content: '';
      position: absolute;
      top: -5px;
      right: -5px;
      width: 12px;
      height: 12px;
      background: #ff4757;
      border-radius: 50%;
      border: 2px solid white;
      animation: ${pulse} 2s infinite;
    }
  `}
`;

const ChatWindow = styled.div`
  width: 350px;
  height: 500px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${props => props.$isClosing ? slideOutDown : slideInUp} 0.3s ease forwards;

  @media (max-width: 480px) {
    width: 320px;
    height: 450px;
    right: 10px;
    bottom: 10px;
  }
`;

const ChatHeader = styled.div`
  background: linear-gradient(135deg, #042e2e, #065454);
  color: white;
  padding: 15px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ChatTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  font-size: 16px;
`;

const StatusIndicator = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #00b894;
  animation: ${pulse} 2s infinite;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 18px;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  transition: background-color 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  background: #f8f9fa;
  display: flex;
  flex-direction: column;
  gap: 15px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #042e2e;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #065454;
  }
`;

const MessageBubble = styled.div`
  max-width: 80%;
  padding: 12px 16px;
  border-radius: 18px;
  line-height: 1.4;
  font-size: 14px;
  position: relative;
  animation: ${slideInUp} 0.3s ease;

  ${props => props.$isUser ? css`
    background: linear-gradient(135deg, #042e2e, #065454);
    color: white;
    align-self: flex-end;
    border-bottom-right-radius: 5px;
  ` : css`
    background: white;
    color: #333;
    align-self: flex-start;
    border: 1px solid #e0e0e0;
    border-bottom-left-radius: 5px;
  `}
`;

const MessageTime = styled.div`
  font-size: 11px;
  opacity: 0.7;
  margin-top: 5px;
  text-align: ${props => props.$isUser ? 'right' : 'left'};
`;

const InputContainer = styled.div`
  padding: 15px 20px;
  background: white;
  border-top: 1px solid #e0e0e0;
  display: flex;
  gap: 10px;
  align-items: center;
`;

const MessageInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 25px;
  outline: none;
  font-size: 14px;
  transition: border-color 0.3s ease;

  &:focus {
    border-color: #042e2e;
  }

  &::placeholder {
    color: #999;
  }
`;

const SendButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #042e2e, #065454);
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    transform: scale(1.1);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const TypingIndicator = styled.div`
  align-self: flex-start;
  background: white;
  padding: 12px 16px;
  border-radius: 18px;
  border: 1px solid #e0e0e0;
  border-bottom-left-radius: 5px;
  font-size: 14px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 5px;

  &::after {
    content: '...';
    animation: typing 1.5s infinite;
  }

  @keyframes typing {
    0%, 20% { content: '.'; }
    40% { content: '..'; }
    60%, 100% { content: '...'; }
  }
`;

const WelcomeMessage = styled.div`
  text-align: center;
  padding: 20px;
  background: white;
  border-radius: 15px;
  border: 1px solid #e0e0e0;
  margin-bottom: 15px;
`;

const WelcomeTitle = styled.h4`
  color: #042e2e;
  margin-bottom: 10px;
  font-weight: 600;
`;

const WelcomeText = styled.p`
  color: #666;
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
`;

// Main Chat Component
function AICeeChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Sample welcome messages from AICEE
  const welcomeMessages = [
    "Hello! I'm AICEE, your virtual assistant. How can I help you today?",
    "Welcome to AICEE! I'm here to assist you with any questions about the examination process, schedules, or general inquiries.",
    "Hi there! I'm AICEE Assistant. Feel free to ask me anything about the examination, registration, or technical support."
  ];

  // Initialize with welcome message
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage = {
        id: Date.now(),
        text: welcomeMessages[Math.floor(Math.random() * welcomeMessages.length)],
        isUser: false,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setMessages([welcomeMessage]);
    }
  }, [isOpen]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleOpenChat = () => {
    setIsOpen(true);
    setIsClosing(false);
    setHasUnread(false);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 300);
  };

  const handleCloseChat = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 250);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim()) return;

    // Add user message
    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      isUser: true,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    // Simulate AI response after delay
    setTimeout(() => {
      const aiResponse = generateAIResponse(inputMessage);
      const aiMessage = {
        id: Date.now() + 1,
        text: aiResponse,
        isUser: false,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      
      setMessages(prev => [...prev, aiMessage]);
      setIsTyping(false);
      
      if (!isOpen) {
        setHasUnread(true);
      }
    }, 1500);
  };

  const generateAIResponse = (userMessage) => {
    const message = userMessage.toLowerCase();
    
    // Sample responses based on common queries
    if (message.includes('hello') || message.includes('hi') || message.includes('hey')) {
      return "Hello! I'm AICEE Assistant. How can I help you with your examination today?";
    }
    
    if (message.includes('exam') || message.includes('schedule')) {
      return "The examination schedule will be announced soon. Please check the official AICEE website or your student dashboard for updates.";
    }
    
    if (message.includes('payment') || message.includes('fee')) {
      return "For payment-related queries, please visit the 'My Payments' section in your student dashboard. You can also contact our support team for payment assistance.";
    }
    
    if (message.includes('result') || message.includes('score')) {
      return "Results are typically announced within 30 days of the examination. You'll be notified via email and can check your results in the student dashboard.";
    }
    
    if (message.includes('contact') || message.includes('support')) {
      return "You can contact our support team through the 'Contact Us' page, or email us at support@aicee.org. We're available Monday to Friday, 9 AM to 6 PM.";
    }
    
    if (message.includes('thank')) {
      return "You're welcome! If you have any more questions, feel free to ask. Good luck with your preparations!";
    }
    
    // Default response
    return "Thank you for your message. For specific queries about examination details, schedules, or technical issues, I recommend checking the official AICEE website or contacting our support team directly for the most accurate information.";
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  return (
    <ChatContainer>
      {!isOpen && (
        <ChatButton 
          onClick={handleOpenChat}
          $hasUnread={hasUnread}
          title="Chat with AICEE"
        >
          <MDBIcon fas icon="comments" />
        </ChatButton>
      )}

      {isOpen && (
        <ChatWindow $isClosing={isClosing}>
          <ChatHeader>
            <ChatTitle>
              <StatusIndicator />
              AICEE Assistant
            </ChatTitle>
            <CloseButton onClick={handleCloseChat} title="Close chat">
              <MDBIcon fas icon="times" />
            </CloseButton>
          </ChatHeader>

          <MessagesContainer>
            {messages.length === 0 && (
              <WelcomeMessage>
                <WelcomeTitle>Welcome to AICEE Chat!</WelcomeTitle>
                <WelcomeText>
                  I'm here to help you with examination queries, schedules, 
                  and general information. How can I assist you today?
                </WelcomeText>
              </WelcomeMessage>
            )}
            
            {messages.map((message) => (
              <div key={message.id}>
                <MessageBubble $isUser={message.isUser}>
                  {message.text}
                </MessageBubble>
                <MessageTime $isUser={message.isUser}>
                  {message.time}
                </MessageTime>
              </div>
            ))}
            
            {isTyping && <TypingIndicator>AICEE is typing</TypingIndicator>}
            <div ref={messagesEndRef} />
          </MessagesContainer>

          <InputContainer as="form" onSubmit={handleSendMessage}>
            <MessageInput
              ref={inputRef}
              type="text"
              placeholder="Type your message..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isTyping}
            />
            <SendButton 
              type="submit" 
              disabled={!inputMessage.trim() || isTyping}
              title="Send message"
            >
              <MDBIcon fas icon="paper-plane" />
            </SendButton>
          </InputContainer>
        </ChatWindow>
      )}
    </ChatContainer>
  );
}

export default AICeeChat;

